import { type HttpError, useList } from 'admin';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Container,
  DataGrid,
  formatCurrency,
  type GridColDef,
  Link,
  Page,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from 'ui';
import { ArrowCircleLeft, ExpandMore } from 'ui/icons';

import { getColorForInvoiceStatus } from '../getColorForInvoiceStatus';
import type { ApoLineItem, InvoiceLineItem } from './types';

const columns: GridColDef<InvoiceLineItem>[] = [
  { field: 'quantity', headerName: 'Quantity' },
  {
    field: 'unit_amount',
    headerName: 'Unit Amount',
  },
  {
    field: 'description',
    headerName: 'Description',
    renderCell: (params) => <Tooltip title={params.value}>{params.value}</Tooltip>,
    width: 300,
  },
  {
    field: 'line_total',
    headerName: 'Line Total',
    valueGetter: (params) => {
      if (params.row.quantity && params.row.unit_amount) {
        return formatCurrency(params.row.quantity * params.row.unit_amount, {
          currencyCode: params.row.invoice_currency_code,
        });
      }
    },
  },
  {
    field: 'invoice_status',
    headerName: 'Invoice Status',
    renderCell: (params) => {
      const statusColor = getColorForInvoiceStatus(params.value);
      return <Chip label={params.value} color={statusColor} />;
    },
    width: 150,
  },
  { field: 'invoice_xero_id', headerName: 'Invoice Xero ID' },
  { field: 'invoice_xero_amount_paid', headerName: 'Invoice Xero Amount Paid' },
  {
    field: 'invoice_xero_fully_paid_on',
    headerName: 'Invoice Xero Fully Paid On',
  },
];

export const DealCostDetails = () => {
  const { deal_id } = useParams();

  const { data: invoiceLineResponse, isLoading: isInvoiceLineLoading } = useList<InvoiceLineItem, HttpError>({
    dataProviderName: 'deals',
    resource: `${deal_id}/costs/`,
  });

  const { data: apoLineResponse, isLoading: isApoLineLoading } = useList<ApoLineItem, HttpError>({
    dataProviderName: 'deals',
    resource: `${deal_id}/cost_apos/`,
  });

  if (isInvoiceLineLoading || isApoLineLoading)
    return (
      <Box sx={{ display: 'flex', mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  const apoLineData = apoLineResponse ? apoLineResponse.data : [];
  const invoiceLineData = invoiceLineResponse ? invoiceLineResponse.data : [];

  return (
    <Page>
      <Box sx={{ mt: 5 }}>
        <Link href={`/post-deal/dealhub/deal/details/${deal_id}/`}>
          <ArrowCircleLeft />
          Back to deal # {deal_id}
        </Link>
      </Box>

      <Box sx={{ height: '80vh', mt: 1, overflow: 'scroll', width: '90vw' }}>
        {apoLineData.map((apoLine: ApoLineItem) => (
          <Accordion defaultExpanded={false} sx={{ mb: 2 }} key={apoLine.id}>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
              <Container>
                <Typography noWrap variant="h6">
                  {apoLine.description}
                </Typography>
              </Container>
            </AccordionSummary>
            <AccordionDetails>
              <Table sx={{ mb: 5 }}>
                <TableHead>
                  <TableRow sx={{ fontWeight: 900 }}>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit Amount</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Currency Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{apoLine.quantity}</TableCell>
                    <TableCell>{apoLine.unit_amount}</TableCell>
                    <TableCell>{apoLine.quantity * apoLine.unit_amount}</TableCell>
                    <TableCell>{apoLine.status}</TableCell>
                    <TableCell>{apoLine.currency_code}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="h6">Invoice Line Items</Typography>
              <DataGrid
                rows={invoiceLineData.filter(
                  (invoiceLine: InvoiceLineItem) => invoiceLine.accounting_purchase_order_line_item_id === apoLine.id
                )}
                columns={columns}
                hideFooter
                sx={{ overflowX: 'scroll' }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Page>
  );
};
